<template>
    <div>
        <loading-indicator v-if="!calendarData"/>
        <div v-else class="xl-padding">
            <div class="flex flex-wrap gap-15">
                <div>
                    <person-box-employee :id="entry.employee_id">
                        Erstellt am {{dateHelper('d.m.Y H:i', entry.created)}}
                    </person-box-employee>
                    <div class="spacer spacer-15 visible-sm-downward">&nbsp;</div>
                </div>

                <div class="flex flex-wrap gap-15">
                    <div class="flex-grow">
                        <strong>Zeitraum</strong>
                        <br>
                        <template v-if="days >= 1">
                            <template v-if="ranges && ranges.length > 1">
                                <div v-for="range in ranges">
                                    {{dateHelper('D, d.m.Y', range.start)}} bis
                                    {{dateHelper('D, d.m.Y', range.end)}}
                                    ({{range.days + ' Tag' + (range.days !== 1 ? 'e' : '')}})
                                </div>
                            </template>
                            <template v-else>
                                {{dateHelper('D, d.m.Y', entry.start)}} bis
                                {{dateHelper('D, d.m.Y', entry.end)}}
                                <template v-if="days">({{days + ' Tag' + (days !== 1 ? 'e' : '')}})</template>
                            </template>
                        </template>
                        <template v-else>
                            {{ dateHelper('D, d.m.Y H:i', entry.start) }} bis
                            {{ dateHelper('H:i', entry.end) }}
                        </template>
                        <div class="spacer spacer-15 visible-sm-downward">&nbsp;</div>
                    </div>

                    <div class="flex-grow">
                        <strong>Resturlaub</strong>
                        <br>
                        {{calendarData.remaining}} Tag{{calendarData.remaining === '1' ? '' : 'e'}}
                    </div>

                    <div class="full-width">
                        <strong>Begründung des Antrags</strong>
                        <br>
                        <div>{{entry.request_reason || 'Keine Begründung angegeben.'}}</div>
                    </div>
                </div>
            </div>

            <div v-if="blocked && blocked.length" class="default-margin-top">
                <div class="info">
                    <i class="material-icons md-light" aria-hidden="true" role="img">info</i>

                    <div>
                        Seit dem Antrag wurden neue gesperrte Tage erstellt:

                        <ul>
                            <li v-for="message in blocked" v-text="message"></li>
                        </ul>
                    </div>
                </div>
            </div>

            <template v-if="0 !== calendarData.regulations.length && !revokeRequest">
                <hr>

                <div v-if="warnings && warnings.length" class="info">
                    <i class="material-icons md-light" aria-hidden="true" role="img">info</i>

                    <div>
                        Bitte beachten Sie, dass an folgenden Tagen Mitarbeiter in der Vertretungsregelung des Antragserstellers bereits abwesend sind:

                        <ul>
                            <li v-for="message in warnings" v-text="message"></li>
                        </ul>
                    </div>
                </div>

                <div class="row">
                    <div class="col-9">
                        <div class="row">
                            <div v-for="(regulation, i) of calendarData.regulations" :class="['col-lg-12', {'spacer-15': 0 !== i}]">
                                <absence-calendar :vacation-request-id="entry.id" :regulation-id="regulation" :show-regulation-title="true" :display-navigation="false"
                                    :show-planning="true" :show-explanation="false" :year="dateHelper('Y', calendarData.start)" tag="div"/>
                            </div>
                        </div>
                    </div>
                    <div class="col-3">
                        <portuna-box class="no-padding no-margin">
                            <table class="portuna-table portuna-table-bordered">
                                <colgroup>
                                    <col style="width: 25%;"/>
                                    <col style="width: 75%;"/>
                                </colgroup>
                                <tbody>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">flight_takeoff</i></td>
                                    <td class="text-center">Urlaub</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">block</i></td>
                                    <td class="text-center">Sonstiges</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">school</i></td>
                                    <td class="text-center">Berufsschule</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">public</i></td>
                                    <td class="text-center">Feiertag</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">local_hospital</i></td>
                                    <td class="text-center">Krank</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">clear</i></td>
                                    <td class="text-center">Nicht eingestellt</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">access_time</i></td>
                                    <td class="text-center">Stundenabbau</td>
                                </tr>
                                <tr>
                                    <td class="text-center"><i class="material-icons" aria-hidden="true" role="img">access_time</i></td>
                                    <td class="text-center">Zeitplanung (Abwesend)</td>
                                </tr>
                                </tbody>
                            </table>
                        </portuna-box>
                    </div>
                </div>
            </template>
        </div>

        <form v-if="!isAccept" ref="form">
            <form-row label="Begründung der Ablehnung" required>
                <form-textarea v-model="comment" class="textarea-vertical"/>
            </form-row>
        </form>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import axios from 'axios';
    import {errorHandler, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import type {TinyEmitter} from 'tiny-emitter';
    import {inject} from 'vue-demi';
    import FormRow from '../../../components/form/FormRow.vue';
    import FormTextarea from '../../../components/form/FormTextarea.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import PersonBoxEmployee from '../../../components/PersonBoxEmployee.vue';
    import PortunaBox from '../../../components/PortunaBox.vue';
    import {parseFormData} from '@/library/form';
    import Loader from '../../../library/intranet/loader';
    import {popupDefaultCloseButton} from '@/library/popup';
    import TopMessageHandler from '../../../library/topMessageHandler';
    import dateHelper from '../../../utils/portuna/dateHelper';
    import AbsenceCalendar from '../../absences/AbsenceCalendar.vue';
    import {ref, computed, watch} from 'vue';

    const portuna      = usePortuna();
    const popupEmitter = inject<TinyEmitter>('popup-emitter');

    const emit = defineEmits<{
        setButtons: [buttons: PopupButton[]];
        decline: [];
        submit: [];
    }>();

    const props = withDefaults(
        defineProps<{
            entry: VacationListEntry;
            action: 'accept' | 'decline';
            revokeRequest: boolean;
        }>(),
        {
            revokeRequest: false,
        },
    );

    const comment      = ref<string>('');
    const start        = ref<string>('');
    const end          = ref<string>('');
    let ranges         = ref<{
        start: string;
        end: string;
        days: number;
    }[]>([]);
    const days         = ref<number>(0);
    const calendarData = ref(null as Nullable<VacationRequestCalendarData>);
    const blocked      = ref<string[]>([]);
    const warnings     = ref<string[]>([]);

    const isAccept = computed(() =>
    {
        return 'accept' === props.action;
    });

    const submit = () =>
    {
        if (!isAccept.value && 0 === comment.value.length)
        {
            TopMessageHandler.error('Es wird eine Begründung für die Ablehnung benötigt.');
            return;
        }
        Loader.show('Bitte warten ...');
        let formData = {
            id: props.entry.id,
            action: props.action,
            comment: comment.value,
        };
        axios
        .post('/employee/vacation/entry/set', parseFormData(formData))
        .then((response) =>
        {
            if (response.data.status !== XHR_STATUS_DEFAULT)
            {
                throw response;
            }
            TopMessageHandler.success(response.data.data.message);
            TopMessageHandler.success(
                `Der Urlaubsantrag wurde ${isAccept.value ? 'genehmigt' : 'abgelehnt'}.`,
            );
            popupEmitter?.emit('emit', {event: 'set'});
            emit('submit');
        })
        .catch(errorHandler)
        .finally(() => Loader.hide());
    };

    const setButtons = () =>
    {
        let buttons: PopupButton[] = [];
        buttons.push(popupDefaultCloseButton);
        if (isAccept.value)
        {
            buttons.push({
                type: 'success',
                title: props.revokeRequest ? 'Zurückziehen genehmigen' : 'Genehmigen',
                icon: 'check_circle',
                cmd: submit,
            });
        }
        buttons.push({
            type: 'danger',
            title: props.revokeRequest ? 'Zurückziehen ablehnen' : 'Ablehnen',
            icon: 'remove_circle',
            cmd: () =>
            {
                if (isAccept.value)
                {
                    popupEmitter?.emit('emit', {event: 'decline'});
                    emit('decline');
                }
                else
                {
                    submit();
                }
            },
        });
        popupEmitter?.emit('setButtons', buttons);
        emit('setButtons', buttons);
    };

    const fetch = () =>
    {
        axios
        .get(`/employee/absences-statistic/fetch-calendar-list/vr_id/${props.entry.id}`)
        .then(({data: {data}}) =>
        {
            calendarData.value = data;
            start.value        = data.start;
            end.value          = data.end;
            ranges.value       = data.ranges;
            days.value         = data.days;
            blocked.value      = data.blocked;
            warnings.value     = data.warnings;
            setButtons();
        });
    };

    fetch();
    watch(() => props.action, setButtons);

    defineExpose(Object.assign({}, portuna));
</script>